import { useCallback, useEffect, useMemo } from "react"
import { observer } from "mobx-react"
import { Box } from "@mui/material"

import { GlobalStore } from "src/store"
import { useStore } from "src/store/lib/useStore"
import { SegmentPickerStore } from "src/components/SegmentPicker/store"
import {
    ChipContainer,
    FilterContainer,
    CategoryTitle,
    StyledChip,
    StyledDivider,
    ChipsFlexContainer,
} from "src/components/SegmentPicker/components/SegmentsPropertyFilterChips/styled"
import {
    ISegmentsPublishGroupOption,
    ISegmentsFilterProps,
} from "src/components/SegmentPicker/components/SegmentsPropertyFilterChips/types"

export const SegmentsPropertyFilterChips = observer(
    ({
        label,
        onChange,
        chipColor,
        sx,
        getOptionLabel = (option) => option.label,
        isOptionEqualToValue = (option, value) => option.id === value.id,
        pathsToMatch = [],
        type,
    }: ISegmentsFilterProps) => {
        const globalStore = useStore(GlobalStore)
        const store = useStore(SegmentPickerStore)

        const currentUrl = window.location.href
        const currentPath = window.location.pathname

        const availableDimensions = useMemo(() => {
            return store.setSegmentsDimensionsByType(type) ?? []
        }, [store, type])

        // Filter selected options to match the current type key
        const filteredSelectedOptions = useMemo(() => {
            return (
                store.selectedSegmentsDimensions?.filter(
                    (option) => option.type === type,
                ) ?? []
            )
        }, [store.selectedSegmentsDimensions, type])

        const allChipActive = useMemo(() => {
            return filteredSelectedOptions.length === 0
        }, [filteredSelectedOptions.length])

        const hasRightSlideIn = useMemo(() => {
            return globalStore.modals.active.some(
                (modal) => modal.options?.variant === "right-slide-in",
            )
        }, [globalStore.modals.active])

        // Check if current URL matches any of the provided paths
        const shouldShowBasedOnPath = useMemo(() => {
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (!pathsToMatch || pathsToMatch.length === 0) {
                return true
            }

            return pathsToMatch.some(
                (path) =>
                    currentUrl.includes(path) || currentPath.includes(path),
            )
        }, [currentPath, currentUrl, pathsToMatch])

        // Check if the current path is product-catalogue/products/:id
        const isProductCataloguePath = useMemo(() => {
            return currentPath.includes("product-catalogue/products/")
        }, [currentPath])

        useEffect(() => {
            // Calculate final visibility based on path match and modal state
            // Skip hasRightSlideIn check for product-catalogue/products/:id
            const shouldBeVisible =
                shouldShowBasedOnPath &&
                (isProductCataloguePath || hasRightSlideIn)

            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (!pathsToMatch || pathsToMatch.length === 0) {
                store.setDimensionsVisible(true)
                return
            }

            store.setDimensionsVisible(shouldBeVisible)
        }, [
            pathsToMatch,
            hasRightSlideIn,
            shouldShowBasedOnPath,
            store,
            isProductCataloguePath,
        ])

        const handleChipClick = useCallback(
            (option: ISegmentsPublishGroupOption) => {
                // Check if this option is already selected
                const isSelected = filteredSelectedOptions.some(
                    (selectedOption) =>
                        isOptionEqualToValue(selectedOption, option),
                )

                let newSelection: ISegmentsPublishGroupOption[] = []

                if (isSelected) {
                    // If it's already selected, deselect it
                    newSelection = filteredSelectedOptions.filter(
                        (selectedOption) =>
                            !isOptionEqualToValue(selectedOption, option),
                    )
                } else {
                    if (allChipActive) {
                        newSelection = [option]
                    } else {
                        newSelection = [...filteredSelectedOptions, option]
                    }

                    if (newSelection.length === availableDimensions.length) {
                        newSelection = [...availableDimensions]
                    }
                }

                onChange(newSelection, type)
            },
            [
                filteredSelectedOptions,
                isOptionEqualToValue,
                onChange,
                type,
                allChipActive,
                availableDimensions,
            ],
        )

        const handleAllClick = useCallback(() => {
            if (!allChipActive) {
                if (filteredSelectedOptions.length === 0) {
                    return
                }

                onChange([], type)
            } else {
                onChange(availableDimensions, type)
            }
        }, [
            allChipActive,
            availableDimensions,
            onChange,
            type,
            filteredSelectedOptions,
        ])

        if (!store.dimensionsVisible) {
            return null
        }

        return (
            <Box key={type} sx={{ mb: 0 }}>
                <FilterContainer sx={sx}>
                    <CategoryTitle>{label}</CategoryTitle>
                    <ChipContainer>
                        <StyledChip
                            label="All"
                            active={allChipActive}
                            onClick={handleAllClick}
                            chipColor={chipColor}
                        />
                        <StyledDivider
                            orientation="vertical"
                            flexItem
                            sx={{ my: "auto" }}
                        />
                        <ChipsFlexContainer>
                            {availableDimensions.map((option) => {
                                const isSelected = filteredSelectedOptions.some(
                                    (selectedOption) =>
                                        isOptionEqualToValue(
                                            selectedOption,
                                            option,
                                        ),
                                )
                                return (
                                    <StyledChip
                                        key={option.id}
                                        label={getOptionLabel(option)}
                                        active={isSelected}
                                        onClick={() => handleChipClick(option)}
                                        chipColor={chipColor}
                                    />
                                )
                            })}
                        </ChipsFlexContainer>
                    </ChipContainer>
                </FilterContainer>
            </Box>
        )
    },
)
